/**
 * Date Range Resolver Utility
 *
 * Pure utility functions for resolving dynamic date range configurations
 * to concrete Date objects.
 */

import { DateRange } from '@angular/material/datepicker';
import {
  DynamicDateRangeConfig,
  DateRangeConfigType,
  ResolvedDateRange,
  DateRangeReference,
  DateRangeUnit,
  DateRangePreset,
  PresetDateRangeConfig,
  RelativeOffsetDateRangeConfig,
  RelativeReferenceDateRangeConfig,
  FixedDateRangeConfig,
} from '../models/date-range-config.model';

/**
 * Resolves a dynamic date range configuration to concrete dates
 *
 * @param config - The dynamic date range configuration
 * @returns Resolved date range with concrete start and end dates
 *
 * @example
 * const config = { type: 'preset', preset: 'last30days', label: 'Last 30 Days' };
 * const resolved = resolveDateRangeConfig(config);
 * // resolved.startDate = 30 days ago
 * // resolved.endDate = today
 */
export function resolveDateRangeConfig(config: DynamicDateRangeConfig): ResolvedDateRange {
  switch (config.type) {
    case DateRangeConfigType.Preset:
      return resolvePreset(config);
    case DateRangeConfigType.RelativeOffset:
      return resolveRelativeOffset(config);
    case DateRangeConfigType.RelativeReference:
      return resolveRelativeReference(config);
    case DateRangeConfigType.Fixed:
      return resolveFixed(config);
    default:
      throw new Error(`Unknown date range config type: ${(config as DynamicDateRangeConfig).type}`);
  }
}

/**
 * Converts a resolved date range to Angular Material DateRange
 */
export function toMaterialDateRange(resolved: ResolvedDateRange): DateRange<Date> {
  return new DateRange(resolved.startDate, resolved.endDate);
}

/**
 * Formats a date to ISO string (YYYY-MM-DD)
 */
export function formatDateToISO(date: Date): string {
  const year = date.getFullYear();
  const month = String(date.getMonth() + 1).padStart(2, '0');
  const day = String(date.getDate()).padStart(2, '0');
  return `${year}-${month}-${day}`;
}

/**
 * Parses an ISO date string to a Date object
 */
export function parseISODate(dateStr: string): Date {
  const [year, month, day] = dateStr.split('-').map(Number);
  return new Date(year, month - 1, day);
}

// =============================================================================
// Preset Resolution
// =============================================================================

function resolvePreset(config: PresetDateRangeConfig): ResolvedDateRange {
  const now = new Date();
  const today = startOfDay(now);
  let startDate: Date;
  let endDate: Date;

  switch (config.preset) {
    case 'today':
      startDate = today;
      endDate = endOfDay(today);
      break;

    case 'yesterday':
      startDate = addDays(today, -1);
      endDate = endOfDay(addDays(today, -1));
      break;

    case 'last7days':
      startDate = addDays(today, -6);
      endDate = endOfDay(today);
      break;

    case 'last30days':
      startDate = addDays(today, -29);
      endDate = endOfDay(today);
      break;

    case 'last60days':
      startDate = addDays(today, -59);
      endDate = endOfDay(today);
      break;

    case 'last90days':
      startDate = addDays(today, -89);
      endDate = endOfDay(today);
      break;

    case 'thisWeek':
      startDate = getStartOfWeek(today);
      endDate = endOfDay(today);
      break;

    case 'lastWeek':
      const lastWeekEnd = addDays(getStartOfWeek(today), -1);
      startDate = getStartOfWeek(lastWeekEnd);
      endDate = endOfDay(lastWeekEnd);
      break;

    case 'thisMonth':
      startDate = getStartOfMonth(today);
      endDate = getEndOfMonth(today);
      break;

    case 'lastMonth':
      const lastMonth = addMonths(today, -1);
      startDate = getStartOfMonth(lastMonth);
      endDate = getEndOfMonth(lastMonth);
      break;

    case 'thisQuarter':
      startDate = getStartOfQuarter(today);
      endDate = getEndOfQuarter(today);
      break;

    case 'lastQuarter':
      const lastQuarter = addMonths(today, -3);
      startDate = getStartOfQuarter(lastQuarter);
      endDate = getEndOfQuarter(lastQuarter);
      break;

    case 'thisYear':
      startDate = getStartOfYear(today);
      endDate = getEndOfYear(today);
      break;

    case 'lastYear':
      const lastYear = new Date(today.getFullYear() - 1, today.getMonth(), today.getDate());
      startDate = getStartOfYear(lastYear);
      endDate = getEndOfYear(lastYear);
      break;

    case 'yearToDate':
      startDate = getStartOfYear(today);
      endDate = endOfDay(today);
      break;

    case 'monthToDate':
      startDate = getStartOfMonth(today);
      endDate = endOfDay(today);
      break;

    case 'weekToDate':
      startDate = getStartOfWeek(today);
      endDate = endOfDay(today);
      break;

    default:
      throw new Error(`Unknown preset: ${config.preset}`);
  }

  return { startDate, endDate, config };
}

// =============================================================================
// Relative Offset Resolution
// =============================================================================

function resolveRelativeOffset(config: RelativeOffsetDateRangeConfig): ResolvedDateRange {
  const today = startOfDay(new Date());

  const startDate = applyOffset(today, config.startOffset, config.startUnit);
  const endDate = endOfDay(applyOffset(today, config.endOffset, config.endUnit));

  return { startDate, endDate, config };
}

function applyOffset(date: Date, offset: number, unit: DateRangeUnit): Date {
  switch (unit) {
    case 'day':
      return addDays(date, offset);
    case 'week':
      return addDays(date, offset * 7);
    case 'month': {
      // Move by months, then align to 1st of that month
      const result = addMonths(date, offset);
      return new Date(result.getFullYear(), result.getMonth(), 1);
    }
    case 'quarter': {
      // Move by quarters, then align to 1st of that quarter
      const result = addMonths(date, offset * 3);
      const quarterMonth = Math.floor(result.getMonth() / 3) * 3;
      return new Date(result.getFullYear(), quarterMonth, 1);
    }
    case 'year':
      // Move by years, then align to January 1st
      return new Date(date.getFullYear() + offset, 0, 1);
    default:
      throw new Error(`Unknown unit: ${unit}`);
  }
}

// =============================================================================
// Relative Reference Resolution
// =============================================================================

function resolveRelativeReference(config: RelativeReferenceDateRangeConfig): ResolvedDateRange {
  const today = new Date();

  const startDate = resolveReference(today, config.startReference, config.startPeriodOffset ?? 0);
  const endDate = resolveReference(today, config.endReference, config.endPeriodOffset ?? 0);

  return { startDate, endDate: endOfDay(endDate), config };
}

function resolveReference(baseDate: Date, reference: DateRangeReference, periodOffset: number): Date {
  let date = new Date(baseDate);

  switch (reference) {
    case 'today':
      return startOfDay(date);

    case 'startOfWeek':
      date = addDays(getStartOfWeek(date), periodOffset * 7);
      return date;

    case 'endOfWeek':
      date = addDays(getEndOfWeek(date), periodOffset * 7);
      return date;

    case 'startOfMonth':
      date = addMonths(date, periodOffset);
      return getStartOfMonth(date);

    case 'endOfMonth':
      date = addMonths(date, periodOffset);
      return getEndOfMonth(date);

    case 'startOfQuarter':
      date = addMonths(date, periodOffset * 3);
      return getStartOfQuarter(date);

    case 'endOfQuarter':
      date = addMonths(date, periodOffset * 3);
      return getEndOfQuarter(date);

    case 'startOfYear':
      return new Date(date.getFullYear() + periodOffset, 0, 1);

    case 'endOfYear':
      return new Date(date.getFullYear() + periodOffset, 11, 31);

    default:
      throw new Error(`Unknown reference: ${reference}`);
  }
}

// =============================================================================
// Fixed Date Resolution
// =============================================================================

function resolveFixed(config: FixedDateRangeConfig): ResolvedDateRange {
  const startDate = parseISODate(config.startDate);
  const endDate = endOfDay(parseISODate(config.endDate));

  return { startDate, endDate, config };
}

// =============================================================================
// Date Utility Functions
// =============================================================================

function startOfDay(date: Date): Date {
  const result = new Date(date);
  result.setHours(0, 0, 0, 0);
  return result;
}

function endOfDay(date: Date): Date {
  const result = new Date(date);
  result.setHours(23, 59, 59, 999);
  return result;
}

function addDays(date: Date, days: number): Date {
  const result = new Date(date);
  result.setDate(result.getDate() + days);
  return result;
}

function addMonths(date: Date, months: number): Date {
  const result = new Date(date);
  result.setMonth(result.getMonth() + months);
  return result;
}

function getStartOfWeek(date: Date): Date {
  const result = new Date(date);
  const day = result.getDay();
  const diff = day === 0 ? -6 : 1 - day; // Monday is first day of week
  result.setDate(result.getDate() + diff);
  result.setHours(0, 0, 0, 0);
  return result;
}

function getEndOfWeek(date: Date): Date {
  const start = getStartOfWeek(date);
  return addDays(start, 6);
}

function getStartOfMonth(date: Date): Date {
  return new Date(date.getFullYear(), date.getMonth(), 1);
}

function getEndOfMonth(date: Date): Date {
  return new Date(date.getFullYear(), date.getMonth() + 1, 0);
}

function getStartOfQuarter(date: Date): Date {
  const quarter = Math.floor(date.getMonth() / 3);
  return new Date(date.getFullYear(), quarter * 3, 1);
}

function getEndOfQuarter(date: Date): Date {
  const quarter = Math.floor(date.getMonth() / 3);
  return new Date(date.getFullYear(), quarter * 3 + 3, 0);
}

function getStartOfYear(date: Date): Date {
  return new Date(date.getFullYear(), 0, 1);
}

function getEndOfYear(date: Date): Date {
  return new Date(date.getFullYear(), 11, 31);
}

// =============================================================================
// Config Builder Utilities
// =============================================================================

/**
 * Creates a preset configuration
 */
export function createPresetConfig(preset: DateRangePreset, label: string): PresetDateRangeConfig {
  return {
    type: DateRangeConfigType.Preset,
    preset,
    label,
  };
}

/**
 * Creates a relative offset configuration
 */
export function createRelativeOffsetConfig(
  label: string,
  startOffset: number,
  startUnit: DateRangeUnit,
  endOffset: number = 0,
  endUnit: DateRangeUnit = 'day'
): RelativeOffsetDateRangeConfig {
  return {
    type: DateRangeConfigType.RelativeOffset,
    label,
    startOffset,
    startUnit,
    endOffset,
    endUnit,
  };
}

/**
 * Creates a fixed date configuration
 */
export function createFixedConfig(label: string, startDate: Date, endDate: Date): FixedDateRangeConfig {
  return {
    type: DateRangeConfigType.Fixed,
    label,
    startDate: formatDateToISO(startDate),
    endDate: formatDateToISO(endDate),
  };
}

/**
 * Creates a configuration from concrete dates, detecting if it matches a preset
 */
export function createConfigFromDates(
  startDate: Date,
  endDate: Date,
  label: string = 'Custom Range'
): DynamicDateRangeConfig {
  // Try to match a preset
  const matchedPreset = detectPreset(startDate, endDate);
  if (matchedPreset) {
    return createPresetConfig(matchedPreset.preset, matchedPreset.label);
  }

  // Fall back to fixed config
  return createFixedConfig(label, startDate, endDate);
}

/**
 * Attempts to detect if the given dates match a standard preset
 */
function detectPreset(startDate: Date, endDate: Date): { preset: DateRangePreset; label: string } | null {
  const today = startOfDay(new Date());
  const start = startOfDay(startDate);
  const end = startOfDay(endDate);

  // Check common presets
  const presets: Array<{ preset: DateRangePreset; label: string; check: () => boolean }> = [
    {
      preset: 'today',
      label: 'Today',
      check: () => isSameDay(start, today) && isSameDay(end, today),
    },
    {
      preset: 'yesterday',
      label: 'Yesterday',
      check: () => {
        const yesterday = addDays(today, -1);
        return isSameDay(start, yesterday) && isSameDay(end, yesterday);
      },
    },
    {
      preset: 'last7days',
      label: 'Last 7 Days',
      check: () => isSameDay(start, addDays(today, -6)) && isSameDay(end, today),
    },
    {
      preset: 'last30days',
      label: 'Last 30 Days',
      check: () => isSameDay(start, addDays(today, -29)) && isSameDay(end, today),
    },
    {
      preset: 'thisMonth',
      label: 'This Month',
      check: () => isSameDay(start, getStartOfMonth(today)) && isSameDay(end, getEndOfMonth(today)),
    },
    {
      preset: 'lastMonth',
      label: 'Last Month',
      check: () => {
        const lastMonth = addMonths(today, -1);
        return isSameDay(start, getStartOfMonth(lastMonth)) && isSameDay(end, getEndOfMonth(lastMonth));
      },
    },
  ];

  for (const p of presets) {
    if (p.check()) {
      return { preset: p.preset, label: p.label };
    }
  }

  return null;
}

function isSameDay(date1: Date, date2: Date): boolean {
  return (
    date1.getFullYear() === date2.getFullYear() &&
    date1.getMonth() === date2.getMonth() &&
    date1.getDate() === date2.getDate()
  );
}

// =============================================================================
// Serialization / Validation
// =============================================================================

/**
 * Type guard: checks if value is a valid DynamicDateRangeConfig
 */
export function isValidDateRangeConfig(value: unknown): value is DynamicDateRangeConfig {
  if (value === null || typeof value !== 'object') return false;
  const o = value as Record<string, unknown>;
  if (typeof o.type !== 'string') return false;
  const r = o as Record<string, unknown>;
  switch (o.type) {
    case DateRangeConfigType.Preset:
      return typeof r['preset'] === 'string' && typeof r['label'] === 'string';
    case DateRangeConfigType.RelativeOffset:
      return (
        typeof r['startOffset'] === 'number' &&
        typeof r['startUnit'] === 'string' &&
        typeof r['endOffset'] === 'number' &&
        typeof r['endUnit'] === 'string' &&
        typeof r['label'] === 'string'
      );
    case DateRangeConfigType.RelativeReference:
      return (
        typeof r['startReference'] === 'string' &&
        typeof r['endReference'] === 'string' &&
        typeof r['label'] === 'string'
      );
    case DateRangeConfigType.Fixed:
      return (
        typeof r['startDate'] === 'string' &&
        typeof r['endDate'] === 'string' &&
        typeof r['label'] === 'string'
      );
    default:
      return false;
  }
}

/**
 * Parses a JSON string to DynamicDateRangeConfig. Throws if invalid.
 */
export function parseDateRangeConfig(json: string): DynamicDateRangeConfig {
  let parsed: unknown;
  try {
    parsed = JSON.parse(json);
  } catch {
    throw new Error('Invalid JSON for date range config');
  }
  if (!isValidDateRangeConfig(parsed)) {
    throw new Error('Invalid date range config structure');
  }
  return parsed;
}
